<template>
  <div class="yardage-tab">
    <div class="tab-header">
      <h4>요척 관리</h4>
      <div v-if="canEdit">
        <el-button size="small" :loading="importing" @click="importFromDesign">
          디자인 요척에서 가져오기
        </el-button>
        <el-button type="primary" size="small" @click="openForm()">
          요척 추가
        </el-button>
      </div>
    </div>

    <el-table
      :data="yardages"
      border
      size="small"
      stripe
      v-loading="loading"
      style="width: 100%"
    >
      <el-table-column prop="id" label="ID" width="50" align="center" />

      <el-table-column label="자재명" min-width="150">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">{{ row.material_name }}</span>
          <el-input
            v-else
            v-model="editForm.material_name"
            size="small"
            placeholder="자재명"
          />
        </template>
      </el-table-column>

      <!-- 원단사 (2026-08-01, 사이먼) — 종이 "아름(#18)"의 '아름'은 원단사명.
           자재명 텍스트에 섞지 않고 공급업체 마스터로 구조화 -->
      <el-table-column label="원단사" width="110">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">{{ row.supplier_name || '-' }}</span>
          <el-select
            v-else
            v-model="editForm.supplier"
            size="small"
            filterable
            clearable
            placeholder="원단사"
            style="width: 100%"
          >
            <el-option v-for="sp in suppliers" :key="sp.supplier_id" :label="sp.name" :value="sp.supplier_id" />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column label="색상" width="100">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">{{ row.color || '-' }}</span>
          <el-input
            v-else
            v-model="editForm.color"
            size="small"
            placeholder="색상"
          />
        </template>
      </el-table-column>

      <el-table-column label="규격(폭)" width="90">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">{{ row.specification || '-' }}</span>
          <el-input
            v-else
            v-model="editForm.specification"
            size="small"
            placeholder="예: 45&quot;"
          />
        </template>
      </el-table-column>

      <el-table-column label="소요량" width="120" align="right">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">
            {{ parseFloat(row.required_quantity).toFixed(3) }}
          </span>
          <el-input-number
            v-else
            v-model="editForm.required_quantity"
            size="small"
            :precision="3"
            :min="0"
            :step="0.1"
            style="width: 100%"
          />
        </template>
      </el-table-column>

      <el-table-column label="단위" width="80" align="center">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">{{ row.unit }}</span>
          <el-select
            v-else
            v-model="editForm.unit"
            size="small"
            style="width: 100%"
            placeholder="단위 선택"
          >
            <el-option label="YD" value="YD" />
            <el-option label="M" value="M" />
            <el-option label="EA" value="EA" />
            <el-option label="KG" value="KG" />
            <el-option label="개" value="개" />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column label="로스율(%)" width="100" align="right">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">
            {{ parseFloat(row.loss_rate).toFixed(2) }}%
          </span>
          <el-input-number
            v-else
            v-model="editForm.loss_rate"
            size="small"
            :precision="2"
            :min="0"
            :max="100"
            :step="0.1"
            style="width: 100%"
          />
        </template>
      </el-table-column>

      <el-table-column label="배색요척" width="100" align="right">
        <template #default="{ row }">
          <span v-if="editingId !== row.id">
            {{ row.accent_yardage ? parseFloat(row.accent_yardage).toFixed(3) : '-' }}
          </span>
          <el-input-number
            v-else
            v-model="editForm.accent_yardage"
            size="small"
            :precision="3"
            :min="0"
            :step="0.1"
            style="width: 100%"
          />
        </template>
      </el-table-column>

      <el-table-column label="합요척" width="110" align="right">
        <template #default="{ row }">
          <el-tooltip placement="top">
            <template #content>
              자동 계산: (소요량 / (100 - 로스율%)) × 100 — 종이 작지의 '합요척'
            </template>
            <span class="total-qty">
              {{ parseFloat(row.total_quantity).toFixed(3) }}
              <el-icon style="margin-left: 4px; font-size: 12px">
                <InfoFilled />
              </el-icon>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- 수량배분 조인 (2026-08-01, 사이먼) — 종이 작지는 요척과 수량이 한 표.
           같은 색상의 수량배분 총수량(+여유분)과 원단 필요량(합요척×수량)을
           읽기전용으로 연결. 색상 불일치는 경고 표시 -->
      <el-table-column label="수량" width="100" align="right">
        <template #default="{ row }">
          <template v-if="colorQtyOf(row.color)">
            {{ formatNumber(colorQtyOf(row.color).qty) }}<span v-if="colorQtyOf(row.color).spare" class="spare-note">+{{ colorQtyOf(row.color).spare }}</span>
          </template>
          <el-tooltip v-else-if="row.color && hasQuantities" content="수량배분 탭에 이 색상이 없습니다 — 색상명 불일치 확인" placement="top">
            <el-tag type="warning" size="small">배분 없음</el-tag>
          </el-tooltip>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>

      <el-table-column label="원단 필요량" width="120" align="right">
        <template #default="{ row }">
          <span v-if="neededOf(row) !== null" class="needed-qty">
            {{ neededOf(row) }} {{ row.unit }}
          </span>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>

      <el-table-column
        v-if="canEdit"
        label="작업"
        width="100"
        align="center"
        fixed="right"
      >
        <template #default="{ row }">
          <span v-if="editingId !== row.id" class="action-buttons">
            <el-button
              type="primary"
              link
              size="small"
              @click="startEdit(row)"
            >
              수정
            </el-button>
            <el-divider direction="vertical" />
            <el-button
              type="danger"
              link
              size="small"
              @click="handleDelete(row)"
            >
              삭제
            </el-button>
          </span>
          <span v-else class="action-buttons">
            <el-button
              type="primary"
              link
              size="small"
              @click="saveEdit(row)"
              :loading="saving"
            >
              저장
            </el-button>
            <el-divider direction="vertical" />
            <el-button
              type="info"
              link
              size="small"
              @click="cancelEdit"
            >
              취소
            </el-button>
          </span>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="yardages.length === 0 && !loading" description="등록된 요척이 없습니다." />

    <!-- 요척 추가 폼 다이얼로그 -->
    <el-dialog
      v-model="formVisible"
      title="요척 추가"
      width="600px"
    >
      <el-form :model="form" label-width="100px" :rules="formRules" ref="formRef">
        <el-form-item label="자재명" prop="material_name">
          <el-input
            v-model="form.material_name"
            placeholder="자재명을 입력하세요"
          />
        </el-form-item>

        <el-form-item label="원단사" prop="supplier">
          <el-select v-model="form.supplier" filterable clearable placeholder="공급업체 선택 (예: 아름)" style="width: 100%">
            <el-option v-for="sp in suppliers" :key="sp.supplier_id" :label="sp.name" :value="sp.supplier_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="색상" prop="color">
          <el-input
            v-model="form.color"
            placeholder="색상을 입력하세요 (선택사항)"
          />
        </el-form-item>

        <el-form-item label="규격(폭)" prop="specification">
          <el-input
            v-model="form.specification"
            placeholder="예: 45&quot;, 53&quot; (선택사항)"
          />
        </el-form-item>

        <el-form-item label="배색요척" prop="accent_yardage">
          <el-input-number
            v-model="form.accent_yardage"
            :precision="3"
            :min="0"
            :step="0.1"
            style="width: 100%"
            placeholder="배색으로 쓰일 때의 장당 소요 (선택사항)"
          />
        </el-form-item>

        <el-form-item label="소요량" prop="required_quantity">
          <el-input-number
            v-model="form.required_quantity"
            :precision="3"
            :min="0"
            :step="0.1"
            style="width: 100%"
            placeholder="소요량을 입력하세요"
          />
        </el-form-item>

        <el-form-item label="단위" prop="unit">
          <el-select
            v-model="form.unit"
            style="width: 100%"
            placeholder="단위를 선택하세요"
          >
            <el-option label="YD" value="YD" />
            <el-option label="M" value="M" />
            <el-option label="EA" value="EA" />
            <el-option label="KG" value="KG" />
            <el-option label="개" value="개" />
          </el-select>
        </el-form-item>

        <el-form-item label="로스율(%)" prop="loss_rate">
          <el-input-number
            v-model="form.loss_rate"
            :precision="2"
            :min="0"
            :max="100"
            :step="0.1"
            style="width: 100%"
            placeholder="로스율을 입력하세요"
          />
        </el-form-item>

        <el-alert
          title="총소요량은 자동으로 계산됩니다"
          type="info"
          :closable="false"
          style="margin-bottom: 12px"
        >
          <template #default>
            총소요량 = (소요량 / (100 - 로스율%)) × 100
          </template>
        </el-alert>
      </el-form>

      <template #footer>
        <el-button @click="formVisible = false">취소</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="saving">
          추가
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { InfoFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({
  mainRequestId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(false)
const saving = ref(false)
const yardages = ref([])
const formVisible = ref(false)
const formRef = ref(null)
const editingId = ref(null)

const defaultForm = () => ({
  material_name: '',
  supplier: null,
  color: '',
  specification: '',
  accent_yardage: null,
  required_quantity: 0,
  unit: 'YD',
  loss_rate: 0,
})

const form = ref(defaultForm())
const editForm = ref({ ...defaultForm() })

const formRules = {
  material_name: [
    { required: true, message: '자재명을 입력하세요', trigger: 'blur' },
  ],
  required_quantity: [
    { required: true, message: '소요량을 입력하세요', trigger: 'blur' },
  ],
  unit: [
    { required: true, message: '단위를 선택하세요', trigger: 'change' },
  ],
}

const fetchYardages = async () => {
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const res = await axiosInstance.get(
      `production/yardages/`,
      { params: { main_request: props.mainRequestId } }
    )
    yardages.value = Array.isArray(res.data) ? res.data : res.data.results || []
  } catch (e) {
    console.error('요척 조회 실패:', e)
    ElMessage.error('요척 조회 실패')
  } finally {
    loading.value = false
  }
}

// 공급업체 목록 (원단사 선택용, 2026-08-01)
const suppliers = ref([])
const fetchSuppliers = async () => {
  try {
    const res = await axiosInstance.get('/suppliers/', { params: { page_size: 1000 } })
    suppliers.value = res.data?.results || res.data || []
  } catch (e) { console.error('공급업체 조회 실패:', e) }
}

// ── 수량배분 조인 (2026-08-01) — 색상별 총수량(+여유분)·원단 필요량 표시 ──
const colorQty = ref({}) // { 색상명: { qty, spare } }
const hasQuantities = ref(false)

const fetchQuantities = async () => {
  if (!props.mainRequestId) return
  try {
    const res = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/size-quantities/`
    )
    const map = {}
    for (const item of res.data.items || []) {
      const c = item.color_name || ''
      if (!map[c]) map[c] = { qty: 0, spare: 0 }
      map[c].qty += item.quantity || 0
      map[c].spare += item.spare_quantity || 0
    }
    colorQty.value = map
    hasQuantities.value = Object.keys(map).length > 0
  } catch (e) {
    console.error('수량배분 조회 실패:', e)
  }
}

const colorQtyOf = (color) => colorQty.value[color] || null

// 원단 필요량 = 합요척 × (총수량+여유분) — 종이 작지에서 손으로 하던 계산
const neededOf = (row) => {
  const cq = colorQtyOf(row.color)
  if (!cq) return null
  const per = parseFloat(row.total_quantity) || 0
  if (!per) return null
  return (per * (cq.qty + cq.spare)).toLocaleString(undefined, { maximumFractionDigits: 1 })
}

const formatNumber = (n) => (n || 0).toLocaleString()

// 부모(메인오더 상세)가 탭 전환 시 호출 — 수량배분 수정 후 최신값 반영
defineExpose({ refreshQuantities: fetchQuantities })

// 디자인 요척 기준표 × 생산수량 → 오더 총소요량 자동 생성 (2026-07-19)
const importing = ref(false)
const importFromDesign = async () => {
  importing.value = true
  try {
    const res = await axiosInstance.post(
      `production/main-requests/${props.mainRequestId}/yardages/from-design/`)
    ElMessage.success(res.data.message || '가져왔습니다.')
    await fetchYardages()
  } catch (e) {
    ElMessage.error(e.response?.data?.error || '가져오기에 실패했습니다.')
  } finally {
    importing.value = false
  }
}

const openForm = () => {
  form.value = defaultForm()
  formVisible.value = true
}

const startEdit = (row) => {
  editingId.value = row.id
  editForm.value = {
    material_name: row.material_name,
    supplier: row.supplier || null,
    color: row.color || '',
    specification: row.specification || '',
    accent_yardage: row.accent_yardage ? parseFloat(row.accent_yardage) : null,
    required_quantity: parseFloat(row.required_quantity),
    unit: row.unit,
    loss_rate: parseFloat(row.loss_rate),
  }
}

const cancelEdit = () => {
  editingId.value = null
  editForm.value = { ...defaultForm() }
}

const saveEdit = async (row) => {
  if (!editForm.value.material_name) {
    ElMessage.warning('자재명을 입력하세요.')
    return
  }
  if (!editForm.value.required_quantity || editForm.value.required_quantity <= 0) {
    ElMessage.warning('소요량을 입력하세요.')
    return
  }
  if (!editForm.value.unit) {
    ElMessage.warning('단위를 선택하세요.')
    return
  }

  saving.value = true
  try {
    const data = {
      main_request: props.mainRequestId,
      material_name: editForm.value.material_name,
      supplier: editForm.value.supplier || null,
      color: editForm.value.color || '',
      specification: editForm.value.specification || '',
      accent_yardage: editForm.value.accent_yardage || null,
      required_quantity: editForm.value.required_quantity,
      unit: editForm.value.unit,
      loss_rate: editForm.value.loss_rate || 0,
    }
    await axiosInstance.put(`production/yardages/${row.id}/`, data)
    ElMessage.success('요척이 수정되었습니다.')
    editingId.value = null
    await fetchYardages()
  } catch (e) {
    ElMessage.error('수정 실패: ' + (e.response?.data?.detail || '오류 발생'))
  } finally {
    saving.value = false
  }
}

const handleSubmit = async () => {
  if (formRef.value) {
    try {
      await formRef.value.validate()
    } catch {
      return
    }
  }
  saving.value = true
  try {
    const data = {
      main_request: props.mainRequestId,
      material_name: form.value.material_name,
      supplier: form.value.supplier || null,
      color: form.value.color || '',
      specification: form.value.specification || '',
      accent_yardage: form.value.accent_yardage || null,
      required_quantity: form.value.required_quantity,
      unit: form.value.unit,
      loss_rate: form.value.loss_rate || 0,
    }
    await axiosInstance.post('production/yardages/', data)
    ElMessage.success('요척이 추가되었습니다.')
    formVisible.value = false
    await fetchYardages()
  } catch (e) {
    ElMessage.error('추가 실패: ' + (e.response?.data?.detail || '오류 발생'))
  } finally {
    saving.value = false
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `"${row.material_name}" 요척을 삭제하시겠습니까?`,
      '삭제 확인',
      {
        confirmButtonText: '삭제',
        cancelButtonText: '취소',
        type: 'warning',
      }
    )
    await axiosInstance.delete(`production/yardages/${row.id}/`)
    ElMessage.success('삭제되었습니다.')
    await fetchYardages()
  } catch (e) {
    if (e.response) {
      ElMessage.error('삭제 실패: ' + (e.response?.data?.detail || '오류 발생'))
    }
  }
}

watch(() => props.mainRequestId, () => { fetchYardages(); fetchQuantities() }, { immediate: true })

onMounted(() => {
  fetchYardages()
  fetchQuantities()
  fetchSuppliers()
})
</script>

<style scoped>
.yardage-tab {
  padding: 8px 0;
}

/* 수량배분 조인 열 (2026-08-01) */
.spare-note { color: #e6a23c; font-weight: 700; font-size: 12px; }
.needed-qty { font-weight: 700; color: var(--sw-primary, #1f3a5f); }
.muted { color: #c0c4cc; }

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.tab-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.total-qty {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #409EFF;
  font-weight: 500;
  cursor: help;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

:deep(.el-input-number__decrease),
:deep(.el-input-number__increase) {
  display: none;
}

:deep(.el-table .el-form-item) {
  margin-bottom: 0;
}
</style>
